import { Event } from './eventsService';

interface CalendarEvent {
  title: string;
  description?: string;
  location?: string;
  startDate: Date;
  endDate: Date;
  allDay?: boolean;
}

// Gerar arquivo .ics para download
export const generateICSFile = (event: CalendarEvent): string => {
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
  };

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Hostly//Calendar//PT',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${formatDate(event.startDate)}`,
    `DTEND:${formatDate(event.endDate)}`,
    `SUMMARY:${event.title}`,
    event.description ? `DESCRIPTION:${event.description}` : '',
    event.location ? `LOCATION:${event.location}` : '',
    `DTSTAMP:${formatDate(new Date())}`,
    `UID:${Date.now()}@hostly.app`,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(line => line).join('\r\n');

  return icsContent;
};

// Adicionar ao Google Calendar
export const addToGoogleCalendar = (event: Event): void => {
  const startDate = new Date(event.date);
  const [hours, minutes] = event.time.split(':');
  startDate.setHours(parseInt(hours), parseInt(minutes));
  
  const endDate = new Date(startDate);
  endDate.setHours(endDate.getHours() + 3); // Evento de 3 horas por padrão

  const formatGoogleDate = (date: Date): string => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    details: event.description || '',
    location: event.location || '',
    dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
  });

  const url = `https://calendar.google.com/calendar/render?${params.toString()}`;
  window.open(url, '_blank');
};

// Adicionar ao Apple Calendar (via arquivo .ics)
export const addToAppleCalendar = (event: Event): void => {
  const startDate = new Date(event.date);
  const [hours, minutes] = event.time.split(':');
  startDate.setHours(parseInt(hours), parseInt(minutes));
  
  const endDate = new Date(startDate);
  endDate.setHours(endDate.getHours() + 3);

  const calendarEvent: CalendarEvent = {
    title: event.title,
    description: event.description,
    location: event.location,
    startDate,
    endDate,
  };

  const icsContent = generateICSFile(calendarEvent);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${event.title.replace(/\s+/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Gerar URL de assinatura do calendário (webcal://)
export const generateCalendarSubscriptionUrl = (userId: string): string => {
  // TODO: Implementar Cloud Function que gera feed ICS público
  // Por enquanto, retorna URL mock
  const baseUrl = window.location.origin;
  return `webcal://${baseUrl.replace('https://', '')}/api/calendar/${userId}.ics`;
};

// Instruções para sincronizar com Google Calendar
export const getGoogleCalendarSyncInstructions = (): string[] => {
  return [
    '1. Abra o Google Calendar no seu computador',
    '2. No lado esquerdo, clique em "+" ao lado de "Outros calendários"',
    '3. Selecione "De URL"',
    '4. Cole a URL do calendário Hostly',
    '5. Clique em "Adicionar calendário"',
  ];
};

// Instruções para sincronizar com Apple Calendar
export const getAppleCalendarSyncInstructions = (): string[] => {
  return [
    '1. Abra o app Ajustes no seu iPhone',
    '2. Toque em "Calendário"',
    '3. Toque em "Contas"',
    '4. Toque em "Adicionar Conta"',
    '5. Selecione "Outra"',
    '6. Toque em "Adicionar Calendário Assinado"',
    '7. Cole a URL do calendário Hostly',
    '8. Toque em "Próximo" e depois em "Salvar"',
  ];
};

// Exportar todos os eventos de um dia
export const exportDayEvents = (events: Event[]): void => {
  if (events.length === 0) return;

  const calendarEvents: CalendarEvent[] = events.map(event => {
    const startDate = new Date(event.date);
    const [hours, minutes] = event.time.split(':');
    startDate.setHours(parseInt(hours), parseInt(minutes));
    
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + 3);

    return {
      title: event.title,
      description: event.description,
      location: event.location,
      startDate,
      endDate,
    };
  });

  // Gerar arquivo ICS com múltiplos eventos
  const icsContent = generateMultiEventICS(calendarEvents);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `eventos_${new Date().toISOString().split('T')[0]}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const generateMultiEventICS = (events: CalendarEvent[]): string => {
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
  };

  const eventBlocks = events.map(event => [
    'BEGIN:VEVENT',
    `DTSTART:${formatDate(event.startDate)}`,
    `DTEND:${formatDate(event.endDate)}`,
    `SUMMARY:${event.title}`,
    event.description ? `DESCRIPTION:${event.description}` : '',
    event.location ? `LOCATION:${event.location}` : '',
    `DTSTAMP:${formatDate(new Date())}`,
    `UID:${Date.now()}-${Math.random()}@hostly.app`,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'END:VEVENT',
  ].filter(line => line).join('\r\n')).join('\r\n');

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Hostly//Calendar//PT',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:Eventos Hostly',
    'X-WR-TIMEZONE:America/Sao_Paulo',
    eventBlocks,
    'END:VCALENDAR',
  ].join('\r\n');
};
